<template>
  <el-config-provider :locale="epLocale">
    <hb-config-provider
      :product-code="baseStore.getProductCode"
      :form-list-code="baseStore.getFormListCode"
      :token="token"
      :env="env"
      :language="lang"
      :headers="headers"
      :baseUrl="baseUrl"
    >
      <router-view />
      <HbDialogWrapper></HbDialogWrapper>
    </hb-config-provider>
  </el-config-provider>
  <WujieVue
    name="SettlementPayment"
    :url="url"
    :product-code="baseStore.getProductCode"
    :alive="false"
    :sync="false"
    width="0"
    height="0"
    :after-mount="markSettlementPaymentReady"
    :load-error="markSettlementPaymentUnavailable"
  ></WujieVue>
</template>

<script setup lang="ts">
import { siteConfig } from '@/utils/site-config'
import {
  HbDialogWrapper,
  HbConfigProvider,
} from '@hgj/storybrooke-business-tools'

import { en, zhCn, vi } from '@hgj/element-plus/es/locale'
import { getCurrentLanguage, getAccessToken } from '@hgj/common-tools'
import { WujieVue } from '@stroybrooke-business-tools/plugins'
import {
  IS_GLOBAL,
  BillLevel,
  BillTypePage,
  ManifestPageType,
  ManifestAction,
} from '@/const/index'
import { fetchModuleStandardAdapter } from '@/api/common'
import to from 'await-to-js'

import useHouseBillStore from '@/store/useHouseBillStore'
import useUserStore from '@/store/useUserStore.ts'
import useBaseStore from '@/store/useBaseStore'
import useTabs from '@/hooks/useTabs'

import { initializeHouseBill, createHouseBill } from '@/utils/house-bill'
import { initializeMasterBill } from '@/utils/master-bill'
import type { ManifestDetail } from '@/typings/bill'
import * as R from 'ramda'
import { ElLoading } from '@hgj/element-plus'
import { useI18n } from 'vue-i18n'
import {
  destroySettlementPayment,
  markSettlementPaymentReady,
  markSettlementPaymentUnavailable,
} from '@/utils/settlement-payment-ready'

const houseBillStore = useHouseBillStore()
const user = useUserStore()
const baseStore = useBaseStore()
const route = useRoute()
const { addTab } = useTabs()
const { locale } = useI18n()

onBeforeUnmount(() => {
  destroySettlementPayment()
})

const headers = computed(() => {
  return {
    Language: locale.value,
  }
})
const baseUrl = computed(() => {
  return import.meta.env.VITE_API_HOST
})
const token = computed(() => {
  return user.getSessionId || getAccessToken()
})
const env = computed(() => {
  return import.meta.env.VITE_APP_ENV_MODE
})
const url = computed(() => {
  return siteConfig.settlementUrl
})

let epLocale = ref(zhCn)
const lang = getCurrentLanguage() || 'zh-CN'
const langMap: { [key: string]: any } = {
  'zh-CN': zhCn,
  vi: vi,
  en: en,
}
epLocale.value = langMap[lang || 'zh-CN']

/**
 * 标准引流数据
 */
async function handleStandardFrom() {
  const { stageId, flowId } = route.query
  if (!stageId) return
  const loadingInstance = ElLoading.service({ fullscreen: true })
  const [err, res] = await to(
    fetchModuleStandardAdapter({
      flowId: flowId as string,
      stageId: stageId as string,
    }),
  )
  loadingInstance.close()
  if (err) return ElMessage.error(err.message)
  const data = res as any
  const isMaster = data.billLevel === BillLevel.MASTER
  let mergeData: ManifestDetail
  if (isMaster) {
    mergeData = initializeMasterBill(data)
  } else {
    initializeHouseBill(data)
    mergeData = R.mergeDeepRight(createHouseBill(), data)
    houseBillStore.detail.mpciPartyId = user.defaultMpciPartyId
  }

  const routeConfig = {
    name: isMaster ? 'MasterDetail' : BillTypePage.Detail,
    params: {
      id: String(Date.now()),
    },
    query: {
      pageType: ManifestPageType.DRAFT_PAGE,
      action: ManifestAction.NEW,
    },
    meta: {
      title: isMaster ? 'masterBill.new' : 'tab.new',
    },
  }

  addTab({
    value: mergeData,
    route: routeConfig,
  })
}
handleStandardFrom()
</script>
<style scoped></style>
